import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import { Activity, Cpu, HardDrive, Wifi, Database, X, BarChart3 } from 'lucide-react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const StatBar = ({ label, value, max, unit, color }) => {
  const pct = max > 0 ? (value / max) * 100 : 0;
  const barColor = pct > 90 ? 'bg-red-500' : pct > 70 ? 'bg-amber-500' : color;
  return (
    <div className="flex items-center gap-2 min-w-0">
      <span className="text-zinc-500 text-[10px] w-8 flex-shrink-0">{label}</span>
      <div className="flex-1 h-1.5 bg-surface rounded-full overflow-hidden min-w-[40px]">
        <div className={`h-full rounded-full transition-all duration-500 ${barColor}`} style={{ width: `${Math.min(pct, 100)}%` }} />
      </div>
      <span className="text-zinc-400 text-[10px] font-mono w-14 text-right flex-shrink-0">{value}{unit}/{max}{unit}</span>
    </div>
  );
};

const ServerMonitor = () => {
  const { user, token } = useAuth();
  const [visible, setVisible] = useState(() => localStorage.getItem('serverMonitor') === 'true');
  const [stats, setStats] = useState(null);

  const fetchStats = useCallback(async () => {
    if (!visible) return;
    try {
      const res = await axios.get(`${API}/server/stats`, { headers: { Authorization: `Bearer ${token}` } });
      setStats(res.data);
    } catch {}
  }, [token, visible]);

  useEffect(() => {
    if (!visible) return;
    fetchStats();
    const interval = setInterval(fetchStats, 5000);
    return () => clearInterval(interval);
  }, [visible, fetchStats]);

  useEffect(() => {
    localStorage.setItem('serverMonitor', visible.toString());
  }, [visible]);

  if (!user?.is_admin) return null;

  if (!visible) {
    return (
      <button onClick={() => setVisible(true)} data-testid="server-monitor-toggle"
        className="fixed top-2 right-2 z-50 bg-surface border border-white/10 rounded-full p-1.5 hover:border-primary/30 transition-colors group"
        title="Monitor del servidor">
        <BarChart3 className="w-3.5 h-3.5 text-zinc-500 group-hover:text-primary" />
      </button>
    );
  }

  return (
    <div className="fixed top-0 right-0 left-0 lg:left-64 bg-surface/95 backdrop-blur-sm border-b border-white/10 px-4 py-1.5 flex items-center gap-4 text-xs z-40" data-testid="server-monitor-bar">
      <div className="flex items-center gap-1.5 flex-shrink-0">
        <Activity className="w-3 h-3 text-primary" />
        <span className="text-zinc-500 text-[10px] font-heading uppercase">Server</span>
      </div>

      {stats ? (
        <>
          <div className="flex items-center gap-1 flex-shrink-0">
            <Cpu className="w-3 h-3 text-zinc-500" />
            <span className={`font-mono text-[10px] ${stats.cpu_percent > 80 ? 'text-red-400' : 'text-zinc-300'}`}>
              {stats.cpu_percent}%
            </span>
          </div>

          <div className="flex-1 max-w-[160px]">
            <StatBar label="RAM" value={stats.ram_used_gb} max={stats.ram_total_gb} unit="G" color="bg-blue-500" />
          </div>

          <div className="flex-1 max-w-[160px]">
            <StatBar label="Disco" value={stats.disk_used_gb} max={stats.disk_total_gb} unit="G" color="bg-purple-500" />
          </div>

          <div className="flex items-center gap-1 flex-shrink-0">
            <Wifi className="w-3 h-3 text-zinc-500" />
            <span className="text-zinc-400 font-mono text-[10px]">{stats.net_sent_mb}M/{stats.net_recv_mb}M</span>
          </div>

          <div className="flex items-center gap-1 flex-shrink-0">
            <Database className="w-3 h-3 text-zinc-500" />
            <span className="text-zinc-400 font-mono text-[10px]">{stats.db_size_mb}MB</span>
          </div>
        </>
      ) : (
        <span className="text-zinc-600 text-[10px]">Cargando...</span>
      )}

      <button onClick={() => setVisible(false)} className="ml-auto flex-shrink-0 text-zinc-600 hover:text-zinc-400">
        <X className="w-3 h-3" />
      </button>
    </div>
  );
};

export default ServerMonitor;
